import { EditProposal } from "../contexts/SettingsContext";

/**
 * Structured edit proposals emitted by the AI inside a delimited JSON block.
 */
export interface EditProposalsBlock {
  operations?: Array<{
    address?: string;
    type?: string;
    value?: string;
  }>;
}

export interface ParsedAiResponse {
  text: string;
  proposals: EditProposal[];
}

const BLOCK_START = "[EDIT_PROPOSALS]";
const BLOCK_END = "[/EDIT_PROPOSALS]";

/**
 * Validate an Excel address: single cell (A1) or range (A1:B5), optionally
 * sheet-qualified (Sheet1!A1).
 */
export const isValidExcelAddress = (address: string): boolean => {
  if (!address || typeof address !== "string") return false;
  const trimmed = address.trim();
  if (trimmed === "") return false;
  // Reject anything that is not a plain address/range (no formulas, no operators).
  return /^(?:[^!]+!)?\$?[A-Za-z]{1,3}\$?[1-9][0-9]*(?::\$?[A-Za-z]{1,3}\$?[1-9][0-9]*)?$/.test(
    trimmed
  );
};

/**
 * Block dangerous DDE-style formulas that execute external commands.
 */
export const isSafeFormula = (value: string): boolean => {
  if (!value || typeof value !== "string") return false;
  const lower = value.trim().toLowerCase();
  if (!lower.startsWith("=")) return true; // plain values are fine
  const ddePrefixes = [
    "=cmd|",
    "=exe|",
    "=powershell|",
    "=shell|",
    "=rundll|",
    "=wmic|",
    "=mshta|",
    "=script|",
    "=com|",
    "=wmimgmt|",
    "=|",
  ];
  return !ddePrefixes.some((prefix) => lower.startsWith(prefix));
};

const isValidProposalType = (type: string): type is "formula" | "value" =>
  type === "formula" || type === "value";

/**
 * Extract and validate [EDIT_PROPOSALS] JSON blocks from an AI response.
 * Returns the clean text (blocks removed) plus validated proposals.
 */
export const parseAiResponse = (response: string): ParsedAiResponse => {
  if (!response) return { text: response || "", proposals: [] };

  const proposals: EditProposal[] = [];
  let text = response;
  const blockRegex = new RegExp(
    `\\s*${BLOCK_START.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}([\\s\\S]*?)${BLOCK_END.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*`,
    "g"
  );

  text = text.replace(blockRegex, (_, rawJson) => {
    let block: EditProposalsBlock;
    try {
      block = JSON.parse(rawJson.trim());
    } catch {
      // Ignore malformed blocks entirely (AI text remains visible).
      return "";
    }

    const ops = Array.isArray(block.operations) ? block.operations : [];
    ops.forEach((op) => {
      const address = (op.address || "").trim();
      const type = (op.type || "").trim();
      const value = op.value !== undefined ? String(op.value).trim() : "";

      if (!isValidExcelAddress(address)) return;
      if (!isValidProposalType(type)) return;
      if (value === "") return;
      if (type === "formula" && !isSafeFormula(value)) return;

      proposals.push({
        id: `${address}-${type}-${proposals.length}-${Date.now()}`,
        address,
        operation: type,
        value,
        status: "pending",
      });
    });

    return "";
  });

  return { text: text.trim(), proposals };
};

/**
 * Split a sheet-qualified address into { sheet, range }.
 */
const splitAddress = (address: string): { sheet: string | null; range: string } => {
  const idx = address.lastIndexOf("!");
  if (idx !== -1) {
    return { sheet: address.slice(0, idx), range: address.slice(idx + 1) };
  }
  return { sheet: null, range: address };
};

/**
 * Apply approved proposals to the workbook.
 * Ranges fill the value/formula across the entire range (relative refs auto-adjust).
 * The 2D array is built to exactly match the range dimensions.
 */
export const applyProposals = async (proposals: EditProposal[]): Promise<void> => {
  if (!proposals || proposals.length === 0) return;

  const MAX_CELLS_PER_RANGE = 10000;

  await Excel.run(async (context) => {
    const items: Array<{ proposal: EditProposal; range: any }> = [];

    for (const proposal of proposals) {
      const { sheet, range: rangeAddress } = splitAddress(proposal.address);

      const range = sheet
        ? context.workbook.worksheets.getItem(sheet).getRange(rangeAddress)
        : context.workbook.worksheets.getActiveWorksheet().getRange(rangeAddress);

      range.load(["rowCount", "columnCount"]);
      items.push({ proposal, range });
    }

    await context.sync();

    for (const { proposal, range } of items) {
      if (proposal.operation === "formula" && !isSafeFormula(proposal.value)) continue;

      const rows = range.rowCount as number;
      const cols = range.columnCount as number;

      if (rows * cols > MAX_CELLS_PER_RANGE) {
        console.warn(
          `Proposal for ${proposal.address} skipped: range too large (${rows} x ${cols}).`
        );
        continue;
      }

      const matrix: string[][] = Array.from({ length: rows }, () =>
        Array<string>(cols).fill(proposal.value)
      );

      if (proposal.operation === "formula") {
        range.formulas = matrix;
      } else {
        range.values = matrix;
      }
    }

    await context.sync();
  });
};

/**
 * Read current cell values for a set of addresses (for diff "before" display).
 */
export const readCurrentValues = async (addresses: string[]): Promise<Record<string, string>> => {
  const result: Record<string, string> = {};
  if (!addresses || addresses.length === 0) return result;

  await Excel.run(async (context) => {
    const loaded: Array<{ address: string; range: any }> = [];

    for (const address of addresses) {
      try {
        const { sheet, range: rangeAddress } = splitAddress(address);
        const range = sheet
          ? context.workbook.worksheets.getItem(sheet).getRange(rangeAddress)
          : context.workbook.worksheets.getActiveWorksheet().getRange(rangeAddress);
        range.load("values");
        loaded.push({ address, range });
      } catch {
        // Cells that cannot be read (e.g. unsupported sheet) are shown as empty.
        result[address] = "";
      }
    }

    await context.sync();

    for (const item of loaded) {
      try {
        const values = item.range.values as unknown[][];
        result[item.address] = values && values[0] ? String(values[0][0] ?? "") : "";
      } catch {
        result[item.address] = "";
      }
    }
  });

  return result;
};

export const isExcelHost = (): boolean =>
  typeof Office !== "undefined" && Office.context.host === Office.HostType.Excel;
